/**
 * Ronde 3 — PR-3 Consignatie-rooster types & Zod-schemas.
 *
 * Bronspec: docs/specs/ronde-3/01-klantafspraken-consignatie.md §3.2, §7.2
 * + master-prompt §1.1 (architectuur-besluit: snapshot via resolveAgreements).
 *
 * Bevat:
 * - ConsignatieStatus enum + labels
 * - DTOs voor rooster + oproep (server → client)
 * - Zod-schemas voor create/update inputs op de API
 *
 * Werkbon.isConsignatie + InvoiceLineCategory.CONSIGNATIE_STANDBY/OPROEP
 * volgen in PR-4 — bewust niet hier.
 */

import { z } from 'zod'

// ─── Enums (matchen Prisma — packages/db/prisma/schema.prisma) ───────────

export const CONSIGNATIE_STATUSSES = ['GEPLAND', 'ACTIEF', 'AFGEROND', 'GEANNULEERD'] as const
export type ConsignatieStatus = (typeof CONSIGNATIE_STATUSSES)[number]

// ─── Labels (NL) ─────────────────────────────────────────────────────────

export const CONSIGNATIE_STATUS_LABELS: Record<ConsignatieStatus, string> = {
  GEPLAND: 'Gepland',
  ACTIEF: 'Actief',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

// ─── DTOs (server → client) ──────────────────────────────────────────────

/** Eén geregistreerde oproep tijdens de week. */
export interface ConsignatieOproepDTO {
  id: string
  roosterId: string
  /** ISO date (YYYY-MM-DD of volledig ISO 8601). */
  datum: string
  /** "HH:mm". */
  startTijd: string
  /** "HH:mm". */
  eindTijd: string
  reisMinuten: number
  reisKm: number
  werkMinuten: number
  /** reisMinuten + werkMinuten — door de service berekend. */
  totaalMinuten: number
  omschrijving: string | null
  werkbonId: string | null
  createdAt: string
  updatedAt: string
}

/** Volledig rooster incl. oproepen (gebruikt op detail-pagina). */
export interface ConsignatieRoosterDTO {
  id: string
  clientId: string
  employeeId: string
  /** ISO-week notatie "YYYY-Www". */
  weekNr: string
  /** Maandag van de week. */
  startDate: string
  /** Zondag van de week. */
  endDate: string
  status: ConsignatieStatus
  /** Decimals als string over de wire. */
  standbyTarief: string
  oproepUurtarief: string
  minimumUren: string
  notities: string | null
  createdAt: string
  updatedAt: string

  /** Optioneel uitgeklapte client (voor UI). */
  client?: {
    id: string
    name: string
    clientNumber: string
  } | null
  /** Optioneel uitgeklapte employee. */
  employee?: {
    id: string
    firstName: string
    lastName: string
  } | null

  /** Lijst oproepen, ascending op datum/startTijd. */
  oproepen: ConsignatieOproepDTO[]
}

/** Compactere variant voor lijstweergave (oproepen samengevat als count). */
export interface ConsignatieRoosterListItemDTO {
  id: string
  clientId: string
  employeeId: string
  weekNr: string
  startDate: string
  endDate: string
  status: ConsignatieStatus
  standbyTarief: string
  oproepUurtarief: string
  minimumUren: string
  notities: string | null
  createdAt: string
  updatedAt: string

  client: {
    id: string
    name: string
    clientNumber: string
  } | null
  employee: {
    id: string
    firstName: string
    lastName: string
  } | null

  /** Aantal oproepen — voor lijst-weergave (zonder volledige oproep-array). */
  oproepenCount: number
}

// ─── Zod-schemas (input-validatie aan server-zijde) ──────────────────────

export const consignatieStatusEnum = z.enum(CONSIGNATIE_STATUSSES)

/**
 * "YYYY-Www" — ISO-8601 week notatie. We accepteren `W1` t/m `W53`.
 * Spec §3.2 noemt voorbeeld "2026-W19".
 */
const WEEK_NR_RE = /^\d{4}-W(0?[1-9]|[1-4]\d|5[0-3])$/
export const weekNrSchema = z
  .string()
  .min(1)
  .max(10)
  .regex(WEEK_NR_RE, 'weekNr moet formaat "YYYY-Www" hebben (bv. "2026-W19")')

/** POST /consignatie/roosters body. */
export const createRoosterSchema = z.object({
  clientId: z.string().min(1),
  employeeId: z.string().min(1),
  weekNr: weekNrSchema,
  /** Overrides — als niet ingevuld komt waarde uit resolveAgreements snapshot. */
  standbyTarief: z.number().finite().min(0).optional().nullable(),
  oproepUurtarief: z.number().finite().min(0).optional().nullable(),
  minimumUren: z.number().finite().min(0).optional().nullable(),
  notities: z.string().max(2000).optional().nullable(),
  /**
   * FO-413 — optionele expliciete BV voor de gegenereerde consignatie-werkbon
   * (moet actief zijn). Afwezig = afgeleid (interne klant > werkbon-type-
   * koppeling); onbepaald = 422, nooit een standby-bon zonder BV.
   */
  entityId: z.string().min(1).optional().nullable(),
})
export type CreateRoosterInput = z.infer<typeof createRoosterSchema>

/**
 * #35 — POST /consignatie/roosters/recurring body. Maakt per ISO-week tussen
 * startWeek en eindWeek (stap `frequentieWeken`) een rooster + (server-side) een
 * consignatie-werkbon. Tarief-overrides gelden voor elke week (snapshot per week).
 */
export const createRecurringRoosterSchema = z.object({
  clientId: z.string().min(1),
  employeeId: z.string().min(1),
  startWeek: weekNrSchema,
  eindWeek: weekNrSchema,
  frequentieWeken: z.number().int().min(1).max(52),
  standbyTarief: z.number().finite().min(0).optional().nullable(),
  oproepUurtarief: z.number().finite().min(0).optional().nullable(),
  minimumUren: z.number().finite().min(0).optional().nullable(),
  notities: z.string().max(2000).optional().nullable(),
  /** FO-413 — optionele expliciete BV voor alle gegenereerde standby-werkbonnen. */
  entityId: z.string().min(1).optional().nullable(),
})
export type CreateRecurringRoosterInput = z.infer<typeof createRecurringRoosterSchema>

/** PATCH /consignatie/roosters/:id body — alleen status + notities mogen wijzigen. */
export const updateRoosterSchema = z.object({
  status: consignatieStatusEnum.optional(),
  notities: z.string().max(2000).optional().nullable(),
})
export type UpdateRoosterInput = z.infer<typeof updateRoosterSchema>

/** "HH:mm" — 00:00 t/m 23:59. */
const TIJD_RE = /^([01]\d|2[0-3]):[0-5]\d$/
export const tijdSchema = z
  .string()
  .min(5)
  .max(5)
  .regex(TIJD_RE, 'Tijd moet formaat "HH:mm" hebben')

/** POST /consignatie/roosters/:id/oproepen body. */
export const createOproepSchema = z.object({
  /** ISO date-only ("YYYY-MM-DD") of full ISO 8601. */
  datum: z
    .string()
    .min(10)
    .refine(
      (v) => /^\d{4}-\d{2}-\d{2}/.test(v) && !Number.isNaN(Date.parse(v)),
      'Datum moet een geldige ISO-datum zijn (YYYY-MM-DD)',
    ),
  startTijd: tijdSchema,
  eindTijd: tijdSchema,
  reisMinuten: z.number().int().min(0).optional(),
  reisKm: z.number().int().min(0).optional(),
  werkMinuten: z.number().int().min(0).optional(),
  omschrijving: z.string().max(2000).optional().nullable(),
  werkbonId: z.string().min(1).optional().nullable(),
})
export type CreateOproepInput = z.infer<typeof createOproepSchema>

export const updateOproepSchema = createOproepSchema.partial()
export type UpdateOproepInput = z.infer<typeof updateOproepSchema>

// ─── FO-123 §D — piket-dekkingsoverzicht (monteur x week) ────────────────────

/** Eén week-kolom in het dekkingsoverzicht. */
export interface ConsignatieDekkingWeek {
  /** ISO-week "YYYY-Www" (canoniek, zero-padded). */
  weekNr: string
  /** Maandag (ISO) van de week. */
  startDate: string
  /** Zondag (ISO) van de week. */
  endDate: string
  /** Geen enkele monteur op piket deze week (gat in de dekking). */
  isGat: boolean
}

/** Eén bezette cel (rooster) voor een monteur in een week. */
export interface ConsignatieDekkingCel {
  roosterId: string
  clientId: string
  clientName: string | null
  status: ConsignatieStatus
}

/** Eén monteur-rij: per weekNr de roosters (0 of 1; >1 = dubbele bezetting). */
export interface ConsignatieDekkingMonteur {
  employeeId: string
  naam: string
  cellen: Record<string, ConsignatieDekkingCel[]>
}

/** Matrix monteur x week + de gemarkeerde gaten. */
export interface ConsignatieDekkingDTO {
  weken: ConsignatieDekkingWeek[]
  monteurs: ConsignatieDekkingMonteur[]
}

/** Query-params voor het dekkingsoverzicht-endpoint. */
export const dekkingQuerySchema = z.object({
  /** ISO date YYYY-MM-DD — start; de matrix begint op de maandag van die week. */
  van: z.string().optional(),
  /** Aantal week-kolommen (default 8). */
  weken: z.coerce.number().int().min(1).max(26).optional(),
  clientId: z.string().min(1).optional(),
})
export type DekkingQuery = z.infer<typeof dekkingQuerySchema>

/** Query-params voor lijst-endpoint. */
export const listRoostersQuerySchema = z.object({
  /** ISO date YYYY-MM-DD — filter op rooster waar endDate >= van. */
  van: z.string().optional(),
  /** ISO date YYYY-MM-DD — filter op rooster waar startDate <= tot. */
  tot: z.string().optional(),
  clientId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  status: consignatieStatusEnum.optional(),
})
export type ListRoostersQuery = z.infer<typeof listRoostersQuerySchema>

// ─── FO-105 (batch-21) — werkbon-zijdige oproepen (meerdere per nacht) ───────
//
// Los van ConsignatieOproepDTO (rooster-zijde): een WerkbonOproep hangt aan een
// standby-`WerkbonDayEntry` en wordt door de monteur op de werkbon geboekt. Het
// voedt zowel facturatie (één OPROEP-regel per oproep) als verloning
// (`werkMinuten` → `dayEntry.hours`). Gedeeld schema = API + portal-form
// valideren met dezelfde regels (CLAUDE.md validatie-conventie).

/** Eén geboekte oproep onder een consignatie-standby-dag (server → client). */
export interface WerkbonOproepDTO {
  id: string
  dayEntryId: string
  werkbonId: string
  /** "HH:mm" op het kwartier. */
  startTijd: string
  /** "HH:mm" op het kwartier; mag < startTijd zijn (oproep over middernacht). */
  eindTijd: string
  /** Server-afgeleide gewerkte minuten (eind - start, nacht-bewust). */
  werkMinuten: number
  reisMinuten: number
  reisKm: number
  omschrijving: string | null
  createdAt: string
  updatedAt: string
}

/**
 * "HH:mm" op het kwartier (minuten 00/15/30/45). Harde billing/payroll-regel:
 * de API versoepelt niet — de portal snapt input op 15 min vóór verzenden.
 */
const KWARTIER_TIJD_RE = /^([01]\d|2[0-3]):(00|15|30|45)$/
export const werkbonOproepTijdSchema = z
  .string()
  .regex(KWARTIER_TIJD_RE, 'Tijd moet op het kwartier vallen (HH:mm, minuten 00, 15, 30 of 45)')

/** Gedeeld basis-schema voor een oproep-mutatie. reis* default 0 op de server. */
export const werkbonOproepBaseSchema = z.object({
  startTijd: werkbonOproepTijdSchema,
  eindTijd: werkbonOproepTijdSchema,
  reisMinuten: z.number().int().min(0).max(24 * 60).optional(),
  reisKm: z.number().int().min(0).max(100_000).optional(),
  omschrijving: z.string().max(2000).optional().nullable(),
})

/**
 * POST /werkbonnen/:id/dagen/:dayEntryId/oproepen — beide tijden verplicht;
 * start != eind (een 0-minuten-oproep is ongeldig). Cross-midnight is toegestaan
 * (eindTijd < startTijd = volgende dag) en wordt server-side afgehandeld.
 */
export const createWerkbonOproepSchema = werkbonOproepBaseSchema.refine(
  (v) => v.startTijd !== v.eindTijd,
  { message: 'Eindtijd mag niet gelijk zijn aan starttijd', path: ['eindTijd'] },
)
export type CreateWerkbonOproepInput = z.infer<typeof createWerkbonOproepSchema>

/**
 * PATCH /werkbonnen/:id/dagen/:dayEntryId/oproepen/:oproepId — alle velden
 * optioneel. Worden beide tijden meegestuurd, dan moeten ze verschillen; de
 * autoritaire start != eind-check op de gemergede waarden gebeurt server-side.
 */
export const updateWerkbonOproepSchema = werkbonOproepBaseSchema.partial().refine(
  (v) => v.startTijd === undefined || v.eindTijd === undefined || v.startTijd !== v.eindTijd,
  { message: 'Eindtijd mag niet gelijk zijn aan starttijd', path: ['eindTijd'] },
)
export type UpdateWerkbonOproepInput = z.infer<typeof updateWerkbonOproepSchema>
